import { useMemo, useRef, useState, type ReactNode } from "react";
import { Popover } from "antd";
import {
  MessageOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import classNames from "classnames";

import type { NeoConversationDTO } from "../../../hexagon/core/ports/driven/neoConversationRepository";
import LoadingCircle from "../LoadingCircle";
import { groupRecentChats } from "./recentChatGroups";
import styles from "./styles.module.scss";

interface RecentChatsMenuProps {
  conversations: NeoConversationDTO[];
  loading: boolean;
  activeId: number | null;
  onSelect: (conversationId: number) => void;
  onNewChat: () => void;
  children: ReactNode;
}

const StatusIcon = ({ conversation }: { conversation: NeoConversationDTO }) => {
  if (conversation.activity_status === "streaming") {
    return (
      <span className={styles.recentIcon}>
        <LoadingCircle />
      </span>
    );
  }
  if (conversation.activity_status === "finished_unviewed") {
    return (
      <span className={styles.recentIcon}>
        <span className={styles.recentUnreadDot} aria-label="Unread reply" />
      </span>
    );
  }
  return <MessageOutlined className={styles.recentIcon} />;
};

/** Header dropdown: search and continue a recent chat inside the drawer. */
const RecentChatsMenu = ({
  conversations,
  loading,
  activeId,
  onSelect,
  onNewChat,
  children,
}: RecentChatsMenuProps) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);
  const groups = useMemo(
    () => groupRecentChats(conversations, query),
    [conversations, query]
  );

  const pick = (action: () => void) => {
    action();
    setOpen(false);
    setQuery("");
  };

  const content = (
    <div className={styles.recentMenu}>
      <label className={styles.recentSearch}>
        <SearchOutlined />
        <input
          ref={searchRef}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search chats…"
          aria-label="Search chats"
        />
      </label>
      <button
        type="button"
        className={styles.recentNewChat}
        onClick={() => pick(onNewChat)}
      >
        <PlusOutlined /> New chat
      </button>
      <div className={styles.recentScroll}>
        {loading && conversations.length === 0 ? (
          <p className={styles.recentEmpty}>Loading chats…</p>
        ) : groups.length === 0 ? (
          <p className={styles.recentEmpty}>
            {query ? "No chats match your search." : "No recent chats yet."}
          </p>
        ) : (
          groups.map((group) => (
            <div key={group.key} className={styles.recentGroup}>
              <div className={styles.recentGroupTitle}>{group.title}</div>
              {group.items.map((conversation) => (
                <button
                  key={conversation.id}
                  type="button"
                  className={classNames(styles.recentItem, {
                    [styles.recentItemActive]: conversation.id === activeId,
                  })}
                  onClick={() => pick(() => onSelect(conversation.id))}
                  title={conversation.title ?? undefined}
                >
                  <StatusIcon conversation={conversation} />
                  <span className={styles.recentTitle}>
                    {conversation.title?.trim() || "New Conversation"}
                  </span>
                </button>
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  );

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) window.setTimeout(() => searchRef.current?.focus(), 0);
        else setQuery("");
      }}
      trigger="click"
      placement="bottomLeft"
      arrow={false}
      content={content}
      rootClassName={styles.recentPopover}
    >
      {children}
    </Popover>
  );
};

export default RecentChatsMenu;
